/* eslint-disable max-lines */
/* eslint-disable max-statements */
import { createAsyncThunk } from '@reduxjs/toolkit';
import DealApi from '../../apis/deal';
import { DealObject } from '../../params/redux/pipeline';
import { errorHandler } from '../../helper/error_handler';
import { fetchDealActivityThunk } from './activity';
import { fetchDealContactThunk } from './contact';
import { generateProspectResultMeta } from '../../helper/generate_prospect_meta';
import { getSinglePipelineDetailThunk } from './pipeline';
import { isErrorWithMessage } from '../../helper';
import { RootState } from '../../redux/store';
import { settingSelectedPipeline } from '../slices/pipeline';
import { DealCloseAndUnclosedParams, DealCustomFieldParams, DealEditParams, DealOrderParams, DealParams, PipelineResponse, TransferDealParams, UpdateDealStatusParams } from '../../params/pipeline';
import { resettingDealLoader, setAddingDeal, setEditDeal, settingSelectedDeal } from '../slices/deal';
import { settingAllStages, settingSelectedStage } from '../slices/stage';

interface DealResponse extends Record<string, unknown> {
    success?: 0 | 1;
    deal?: DealObject;
    msg?: string;
    token?: string;
}

export const fetchDealDetailThunk = createAsyncThunk(
    'deal/detail',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: DealResponse = await DealApi.fetchDealDetail(state.app.token, id);
            if (response?.success === 1) {
                const { deal } = response;
                if (deal) {
                    deal.prospect_result_meta = generateProspectResultMeta(response?.deal as DealObject);
                }
                await dispatch(fetchDealContactThunk()).unwrap();
                dispatch(settingSelectedDeal(response?.deal));
                return response?.deal;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const createDealThunk = createAsyncThunk(
    'deal/create',
    async (data: DealParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.createDeal(state.app.token, data);
            if (response?.success === 1) {
                dispatch(setAddingDeal(false));
                dispatch(settingSelectedStage(null));
                await dispatch(getSinglePipelineDetailThunk(parseInt(data.pipeline_id, 10))).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const updateDealOrderThunk = createAsyncThunk(
    'deal/updateorder',
    async (data: DealOrderParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: PipelineResponse = await DealApi.updateDealOrder(state.app.token, data) as PipelineResponse;
            if (response?.success === 1) {
                dispatch(settingSelectedPipeline(response.pipeline));
                dispatch(settingAllStages(response.pipeline?.pipeline_stages));
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const getArchievedDealsThunk = createAsyncThunk(
    'deal/archieved',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.getArchievedDeals(state.app.token, state.pipeline.selectedPipeline?.id || 0);
            if (response?.success === 1) {
                return response.deals;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const transferDealThunk = createAsyncThunk(
    'deal/transfer',
    async (data: TransferDealParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.transferDeal(state.app.token, data);
            if (response?.success === 1) {
                if (state.deal.flags.showArchievedList) {
                    await dispatch(getArchievedDealsThunk()).unwrap();
                } else {
                    await dispatch(getSinglePipelineDetailThunk(state.pipeline.selectedPipeline?.id as number)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const updateDealStatusThunk = createAsyncThunk(
    'deal/updatestatus',
    async (data: UpdateDealStatusParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.updateDealStatus(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const dealCustomFieldsUpdateThunk = createAsyncThunk(
    'deal/customfieldupdate',
    async (data: DealCustomFieldParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.dealCustomFieldsUpdate(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const restoreDealThunk = createAsyncThunk(
    'deal/restore',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.restoreDeal(state.app.token, id);
            if (response?.success === 1) {
                // await dispatch(getSinglePipelineDetailThunk(state.pipeline.selectedPipeline?.id as number)).unwrap();
                await dispatch(getArchievedDealsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const editDealThunk = createAsyncThunk(
    'deal/edit',
    async (data: DealEditParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.editDeal(state.app.token, data);
            if (response?.success === 1) {
                dispatch(setEditDeal(false));
                await dispatch(fetchDealDetailThunk(data.deal_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const closeAndUnCloseDealThunk = createAsyncThunk(
    'deal/unclose-close',
    async ({ data, client_id }: { data: DealCloseAndUnclosedParams, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.closeAndUnCloseDeal(state.app.token, data, client_id);
            if (response?.success === 1) {
                await dispatch(fetchDealDetailThunk(data.deal_id)).unwrap();
                return response?.client_id;
            }
            return errorHandler(response?.msg as string || 'Updating deal failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const fetchAllDealsThunk = createAsyncThunk(
    'deal/fetch-all',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.fetchAllDeals(state.app.token);
            if (response?.success === 1) {
                return response?.deals;
            }
            return errorHandler(response?.msg as string || 'fetching deals failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const fetchAllDealFilesThunk = createAsyncThunk(
    'deal/fetch-files',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.fetchDealFiles(state.app.token, state.deal.selectedDeal?.id || 0);
            if (response?.success === 1) {
                return response?.deal_files;
            }
            return errorHandler(response?.msg as string || 'fetching deals failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const createDealFileThunk = createAsyncThunk(
    'deal/create-file',
    async ({ file, file_name }: { file: string, file_name: string }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.addDealFile(state.app.token, file, file_name, state.deal.selectedDeal?.id || 0);
            if (response?.success === 1) {
                await dispatch(fetchDealActivityThunk()).unwrap();
                return response?.deals;
            }
            return errorHandler(response?.msg as string || 'fetching deals failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const deleteDealFileThunk = createAsyncThunk(
    'deal/delete-file',
    async (fileId: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.deleteDealFiles(state.app.token, state.deal.selectedDeal?.id || 0, fileId);
            if (response?.success === 1) {
                await dispatch(fetchDealActivityThunk()).unwrap();
                return response?.deals;
            }
            return errorHandler(response?.msg as string || 'fetching deals failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);

export const editDealFileThunk = createAsyncThunk(
    'deal/edit-file',
    async ({ fileId, file_name }: { fileId: number, file_name: string }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await DealApi.editDealFiles(state.app.token, state.deal.selectedDeal?.id || 0, fileId, file_name);
            if (response?.success === 1) {
                await dispatch(fetchDealActivityThunk()).unwrap();
                return response?.deals;
            }
            return errorHandler(response?.msg as string || 'fetching deals failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingDealLoader());
        }
    }
);